"use client";

import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Sheet,
  SheetClose,
  SheetContent,
  SheetFooter,
  SheetTrigger,
} from "@/components/ui/sheet";
import { Title, Paragraph, Caption } from "@/components/ui/typography";
import { AlertCircle, Clock, X } from "lucide-react";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormMessage,
} from "@/components/ui/form";
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import { z } from "zod";
import { Label } from "@/components/ui/label";
import { Separator } from "@/components/ui/separator";
import { useRouter } from "next/navigation";

const OnSuccess = (setOpen,setShowRefundSuccess) => {
  return (
    <>
      <div className="h-[calc(100%-80px)]  flex flex-col gap-3 justify-center items-center relative">
        <div className="flex items-center justify-center border border-midnightIndigo-150 rounded p-2">
          <Clock className="w-6 h-6 " />
        </div>
        <Title size="lg">Refund request received</Title>
        <Caption className="text-center">
          We'll review your refund request promptly. The refund will be
          processed and credited to your provided bank details. This refund may
          take up to 10 working days to reflect in your balance.
        </Caption>
        <Caption className="text-center">
          We appreciate your patience during this process.
        </Caption>

        <Button
          onClick={() => {
            setOpen(false);
            setShowRefundSuccess(false);
            // toggleDrawer(false);
          }}
          variant="outline"
          size="md"
          className="mt-6 leading-[0] hidden lg:block"
        >
          Go Back
        </Button>
      </div>

      <div className="flex flex-col relative lg:hidden">
        <Separator className="w-[calc(100%+48px)] absolute top-0 -left-6" />
        <Button
          onClick={() => {
            // setOpen(false);
            // toggleDrawer(false);
            setShowRefundSuccess(false);
          }}
          variant="outline"
          size="md"
          className="!w-full  my-6"
        >
          Go Back
        </Button>
      </div>
    </>
  );
};
const schema = z.object({
  bank_name: z.string({ required_error: "Bank Name is required" }).min(5,{ message: "Bank Name must contain atleast 5 letters" }),
  bank_address: z.string({ required_error: "Bank Address is required" }).min(5,{ message: "Bank Address must contain atleast 5 letters" }),
  beneficiary_name: z.string({ required_error: "Beneficiary Name is required" }).min(5,{ message: "Beneficiary Name must contain atleast 5 letters" }),
  routing_number: z
    .string({ required_error: "Routing Number is required" })
    .min(9, { message: "Routing Number must be exactly 9 digits" }),
  swift_code: z
    .string({ required_error: "SWIFT Code is required" })
    .min(8, { message: "SWIFT Code must be at least 8 characters" })
    .max(11, { message: "SWIFT Code must be at most 11 characters" }),
  account_number: z
    .string({ required_error: "Account Number is required" })
    .min(8, { message: "Account Number must be at least 8 characters" })
    .max(34, { message: "Account Number must be at most 34 characters" }),
});

// const schema = z.object({
//   bank_name: z.string().min(1, { required_error: "Bank Name is required" }),
//   bank_address: z.string().min(1, { message: "Bank Address is required" }),
//   beneficiary_name: z
//     .string()
//     .min(1, { message: "Beneficiary Name is required" }),
//     routing_number: z
//     .string()
//     .min(9, { message: "Routing Number must be at least 9 digits" })
//     .max(9, { message: "Routing Number must be exactly 9 digits" }),
//     swift_code: z
//     .string()
//     .min(8, { message: "Swift Code must be at least 8 characters" })
//     .max(11, { message: "Swift Code must be at most 11 characters" }),
//     account_number: z
//     .string()
//     .min(8, { message: "Account Number must be at least 8 characters" })
//     .max(34, { message: "Account Number must be at most 34 characters" }),
// });
const CustomDrawer = ({ isDrawerOpen, toggleDrawer, handleRefundRequest,orderDetails,setDrawerOpen }) => {
  const [open, setOpen] = useState(false);
  const [showRefundSuccess, setShowRefundSuccess] = useState(false);
  const router = useRouter();

  let BankDetails = orderDetails?.EcomUserBank || {};

  useEffect(() => {
    setOpen(isDrawerOpen);
  }, [isDrawerOpen]);

  useEffect(()=>{
    setDrawerOpen(open);
  },[open]);

  const form = useForm({
    resolver: zodResolver(schema),
    defaultValues: {},
  });
;
  
  useEffect(() => {
    if (orderDetails?.EcomUserBank) {
      const updatedValues = {
        bank_name: BankDetails.bank_name || "",
        bank_address: BankDetails.bank_address || "",
        beneficiary_name: BankDetails.beneficiary_name || "",
        routing_number: BankDetails.routing_number || "",
        swift_code: BankDetails.swift_code || "",
        account_number: BankDetails.account_no || "",
      };
      form.reset(updatedValues); // Dynamically update the form values
    }
  }, [orderDetails, BankDetails,isDrawerOpen, form]);
  
  async function handleSubmit(e) {
    e.preventDefault();
    const responseValue = await handleRefundRequest(form.getValues());
    if (responseValue === "Refund request mail sent") {
      setShowRefundSuccess(true);
      router.push("/orders");
    }
  }
  const keyMapping = {
    bank_name: "bank_name",
    bank_address: "bank_address",
    beneficiary_name: "beneficiary_name",
    routing_number: "routing_number",
    swift_code: "swift_code",
    account_number: "account_no", // Map to `account_no`
  };
  console.log(form.formState.errors,"errors")
  return (
    <Sheet open={open} onOpenChange={setOpen}>
      <SheetTrigger asChild></SheetTrigger>
      <SheetContent
        hideClose
        className="w-screen md:!max-w-[574px] overflow-y-auto scrollbar-hide"
      >
        <SheetClose className="absolute right-4 top-[20px]">
          <X className="w-5 h-5 text-midnightIndigo-300" />
        </SheetClose>
        {showRefundSuccess ? (
          OnSuccess(setOpen, setShowRefundSuccess)
        ) : (
          <>
            <div className="mb-4 lg:mb-6">
              <Title className="mb-2 lg:mb-3">Request refund</Title>
              <Paragraph className="text-midnightIndigo-300">
                To request a refund, please provide us with your bank details
                where the refund transfer should be made.
              </Paragraph>
            </div>
            <div>
              <Form {...form}>
                <form  onSubmit={form.handleSubmit(handleSubmit)} className="mb-4" noValidate>
                  {[
                    { name: "bank_name", label: "Bank name" },
                    { name: "bank_address", label: "Bank address" },
                    { name: "beneficiary_name", label: "Beneficiary name" },
                    { name: "routing_number", label: "Routing number" },
                    { name: "account_number", label: "Account number" },
                    { name: "swift_code", label: "SWIFT Code" },
                  ].map(({ name, label }) => (
                    <FormField
                      key={name}
                      control={form.control}
                      name={name}
                      render={({ field }) => (
                        <FormItem className="mb-4 lg:mb-6">
                          <Label htmlFor={field.name}>{label}</Label>
                          <FormControl>
                            <Input
                              className={`w-full h-[36px] lg:h-[42px] ${
                                form.formState.errors[name] ? "input-error" : ""
                              }`}
                              {...field}
                              disabled={!!BankDetails[keyMapping[name]]}
                            />
                          </FormControl>
                          {form.formState.errors[name] && (
                            <FormMessage>
                              {`${form.formState.errors[name]?.message}`}
                            </FormMessage>
                          )}
                        </FormItem>
                      )}
                    />
                  ))}
                    {
                      BankDetails && Object.keys(BankDetails).length === 0 &&
                      <div className="flex flex-col lg:flex-row justify-between gap-3">
                        <Button
                          variant="outline"
                          size="md"
                          onClick={() => {
                            setOpen(false);
                            toggleDrawer(false);
                          }}
                          className="!w-full leading-[0]"
                        >
                          Cancel
                        </Button>
                        <Button
                          type="submit"
                          variant="primary"
                          size="md"
                          className="!w-full leading-[0]"
                          // onClick={handleSubmit}
                        >
                          Submit bank details
                        </Button>
                      </div>
                    }
                </form>
              </Form>
              <div className="flex gap-2 bg-system-azure rounded p-4">
                <AlertCircle className="w-5 h-5 rotate-180" />
                <Caption className="text-midnightIndigo-400 wireless-Medium">
                  Usually, it takes up to 10 working days to process a refund.
                </Caption>
              </div>
            </div>
          </>
        )}
      </SheetContent>
    </Sheet>
  );
};

export default CustomDrawer;


